/**
 * 마켓 리스트 탭 결정 로직 (dep-free 순수 모듈).
 * bunja/atom 없이 값만으로 다음 탭을 계산하므로 CI에서 안전하게 단위 테스트 가능하다.
 */
export type TabType = 'favorites' | 'perpetual' | 'tradFi' | 'spot';

interface TabContext {
  isSpotOnly: boolean;
  isSpotPath: boolean;
}

/**
 * 종목 컨텍스트 기준 기본 탭 (탐색 모드).
 * - Spot 전용/Spot 경로: spot
 * - TradFi 종목: tradFi
 * - 그 외: perpetual
 */
export const getDefaultTab = ({
  isSpotOnly,
  isSpotPath,
  isTradFiInstrument,
}: TabContext & { isTradFiInstrument: boolean }): TabType => {
  if (isSpotOnly || isSpotPath) return 'spot';
  if (isTradFiInstrument) return 'tradFi';
  return 'perpetual';
};

/**
 * 검색 모드 진입 시 기준 탭.
 * defaultTab과 달리 TradFi 종목이어도 tradFi로 붙지 않고 perpetual로 진입한다.
 * - Spot 전용/Spot 경로: spot
 * - 그 외: perpetual
 */
export const getSearchEntryTab = ({ isSpotOnly, isSpotPath }: TabContext): TabType => {
  if (isSpotOnly || isSpotPath) return 'spot';
  return 'perpetual';
};

/**
 * 검색 모드 '없음↔있음' 전환 시 이동할 탭을 계산한다.
 * 반환값이 null이면 탭을 바꾸지 않는다 (전환이 없거나 이미 목표 탭인 경우).
 * - 탐색 → 검색(없음→있음): 사용자가 선택한 탭(예: TradFi)을 무시하고 searchEntryTab으로 전환.
 * - 검색 → 탐색(있음→없음): 종목 컨텍스트 기본 탭(defaultTab)으로 복원.
 * - 검색 도중 상태(있음→있음, 없음→없음)에는 개입하지 않아 사용자 탭 전환을 유지한다.
 */
export const resolveTabOnSearchModeChange = ({
  hasSearch,
  hadSearch,
  currentTab,
  searchEntryTab,
  defaultTab,
}: {
  hasSearch: boolean;
  hadSearch: boolean;
  currentTab: TabType;
  searchEntryTab: TabType;
  defaultTab: TabType;
}): TabType | null => {
  if (hasSearch === hadSearch) return null;
  const nextTab = hasSearch ? searchEntryTab : defaultTab;
  return currentTab !== nextTab ? nextTab : null;
};
